import { PageHeader } from '@/shared/components/ui/PageHeader';
import { useState, useMemo, useCallback, useRef } from 'react';
import type { WheelEvent as ReactWheelEvent, PointerEvent as ReactPointerEvent } from 'react';
import { ChevronDown, ChevronRight, Search, Users, UserCog, ZoomIn, ZoomOut, Maximize } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { Badge } from '@/shared/components/ui/Badge';
import { Spinner } from '@/shared/components/ui/Spinner';
import { EmptyState } from '@/shared/components/ui/EmptyState';
import { Modal } from '@/shared/components/ui/Modal';
import { useUser } from '@/shared/hooks/useAuth';
import { api, getApiErrorMessage } from '@/shared/api/axios';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

// ─── Types ────────────────────────────────────────────────────────────────────

interface OrgNode {
  id: string;
  display_name: string;
  role: string;
  department: string;
  position: string;
  manager_id: string | null;
  team_leader_of: string;
  children: OrgNode[];
}

interface OrgChartResponse {
  roots: OrgNode[];
  nodeCount: number;
}

// ─── Hooks ────────────────────────────────────────────────────────────────────

function useOrgChart() {
  return useQuery({
    queryKey: ['org-chart'],
    queryFn: async () => {
      const { data } = await api.get<OrgChartResponse>('/api/org-chart');
      return data;
    },
  });
}

function useReassignManager() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ userId, managerId }: { userId: string; managerId: string | null }) => {
      const { data } = await api.patch<{ ok: boolean }>(`/api/admin/users/${userId}/manager`, {
        manager_id: managerId,
      });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-chart'] });
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    },
  });
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const ROLE_LABELS: Record<string, string> = {
  superadmin: 'Super Admin',
  admin: 'Admin',
  manager: 'Manager',
  team_lead: 'Team Lead',
  department_admin: 'Dept Admin',
  bde: 'BDE',
  developer: 'Developer',
  employee: 'Employee',
};

const ROLE_COLORS: Record<string, string> = {
  superadmin: '#7c3aed',
  admin: '#2563eb',
  manager: '#0891b2',
  team_lead: '#059669',
  department_admin: '#d97706',
  bde: '#dc2626',
  developer: '#4f46e5',
  employee: '#6b7280',
};

const DEPT_LABELS: Record<string, string> = {
  sales: 'Sales',
  hr: 'HR',
  it: 'IT',
  digital_marketing: 'Digital Mktg',
  accounts: 'Accounts',
  management: 'Management',
};

function flattenTree(nodes: OrgNode[]): OrgNode[] {
  const result: OrgNode[] = [];
  function walk(node: OrgNode) {
    result.push(node);
    node.children.forEach(walk);
  }
  nodes.forEach(walk);
  return result;
}

function nodeMatchesSearch(node: OrgNode, query: string): boolean {
  const q = query.toLowerCase();
  return (
    node.display_name.toLowerCase().includes(q) ||
    node.department.toLowerCase().includes(q) ||
    node.position.toLowerCase().includes(q) ||
    (ROLE_LABELS[node.role] ?? node.role).toLowerCase().includes(q)
  );
}

function treeContainsMatch(node: OrgNode, query: string): boolean {
  if (nodeMatchesSearch(node, query)) return true;
  return node.children.some((child) => treeContainsMatch(child, query));
}

// ─── Zoom/Pan constants ───────────────────────────────────────────────────────
// The tree has no natural size cap — as more employees are added it can grow
// wider/taller than the viewport. There's no pan-zoom library in this project,
// so zoom/pan is implemented with plain state + a CSS transform (2026-08-16).
const MIN_SCALE = 0.3;
const MAX_SCALE = 2;
const ZOOM_STEP = 0.15;

// ─── Main OrgChart Component ──────────────────────────────────────────────────

export function OrgChart() {
  const user = useUser();
  const isAdmin = user?.role === 'superadmin' || user?.role === 'admin';
  const { data, isLoading, isError } = useOrgChart();
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [reassignTarget, setReassignTarget] = useState<OrgNode | null>(null);

  // Zoom/pan viewport state — see MIN_SCALE/MAX_SCALE comment above.
  const [scale, setScale] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const viewportRef = useRef<HTMLDivElement>(null);
  const dragState = useRef<{ startX: number; startY: number; panX: number; panY: number } | null>(null);
  const [isPanning, setIsPanning] = useState(false);

  const zoomIn = useCallback(() => setScale((s) => Math.min(MAX_SCALE, +(s + ZOOM_STEP).toFixed(2))), []);
  const zoomOut = useCallback(() => setScale((s) => Math.max(MIN_SCALE, +(s - ZOOM_STEP).toFixed(2))), []);
  const resetView = useCallback(() => {
    setScale(1);
    setPan({ x: 0, y: 0 });
  }, []);

  const handleWheel = useCallback((e: ReactWheelEvent<HTMLDivElement>) => {
    if (!e.ctrlKey && !e.metaKey) return; // let normal scroll behave normally unless zoom-modifier held
    e.preventDefault();
    setScale((s) => {
      const next = e.deltaY < 0 ? s + ZOOM_STEP : s - ZOOM_STEP;
      return Math.min(MAX_SCALE, Math.max(MIN_SCALE, +next.toFixed(2)));
    });
  }, []);

  const handlePointerDown = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    // Ignore drags that start on interactive elements (buttons, inputs) inside the tree.
    if ((e.target as HTMLElement).closest('button, input, select, a')) return;
    dragState.current = { startX: e.clientX, startY: e.clientY, panX: pan.x, panY: pan.y };
    setIsPanning(true);
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
  }, [pan]);

  const handlePointerMove = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    if (!dragState.current) return;
    const dx = e.clientX - dragState.current.startX;
    const dy = e.clientY - dragState.current.startY;
    setPan({ x: dragState.current.panX + dx, y: dragState.current.panY + dy });
  }, []);

  const handlePointerUp = useCallback(() => {
    dragState.current = null;
    setIsPanning(false);
  }, []);

  const toggleCollapse = useCallback((id: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const allUsers = useMemo(() => {
    if (!data) return [];
    return flattenTree(data.roots);
  }, [data]);

  const departmentBreakdown = useMemo(() => {
    const counts = new Map<string, number>();
    for (const u of allUsers) {
      const dept = u.department || 'unassigned';
      counts.set(dept, (counts.get(dept) ?? 0) + 1);
    }
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
  }, [allUsers]);

  if (isLoading) return <div className="flex h-full items-center justify-center"><Spinner /></div>;
  if (isError || !data) return <EmptyState title="Failed to load org chart" description="Check your connection." />;
  if (data.roots.length === 0) return <EmptyState title="No employees found" description="Add users to see the organization chart." />;

  return (
    <div className="flex h-full flex-col gap-4 overflow-hidden p-6">
      {/* Header */}
      <PageHeader
        icon={Users}
        eyebrow="Administration"
        title="Organization Chart"
        description={<>{data.nodeCount} team member{data.nodeCount !== 1 ? 's' : ''}</>}
        actions={
          <div className="w-[260px]">
                  <Input
                    name="org-search"
                    placeholder="Search name, role, department…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    leftIcon={<Search className="h-4 w-4" />}
                  />
                </div>
        }
      />

      {/* Department breakdown */}
      {departmentBreakdown.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {departmentBreakdown.map(([dept, count]) => (
            <span
              key={dept}
              className="inline-flex items-center gap-1.5 rounded-full border border-outline bg-surface px-3 py-1 text-body-sm"
            >
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: DEPT_LABELS[dept] ? '#2563eb' : '#6b7280' }} />
              <span className="text-on-surface-variant">{DEPT_LABELS[dept] ?? dept}</span>
              <span className="font-semibold text-on-surface">{count}</span>
            </span>
          ))}
        </div>
      )}

      {/* Tree — zoomable/pannable viewport so large org charts never overflow the page */}
      <div
        ref={viewportRef}
        className={`relative flex-1 overflow-hidden rounded-card border border-outline bg-surface-container-low/30 ${
          isPanning ? 'cursor-grabbing' : 'cursor-grab'
        }`}
        onWheel={handleWheel}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
      >
        {/* Zoom controls */}
        <div className="absolute right-3 top-3 z-10 flex flex-col overflow-hidden rounded border border-outline bg-surface shadow-sm">
          <button
            type="button"
            onClick={zoomIn}
            disabled={scale >= MAX_SCALE}
            className="flex h-8 w-8 items-center justify-center text-on-surface-variant hover:bg-surface-variant disabled:opacity-40"
            aria-label="Zoom in"
            title="Zoom in"
          >
            <ZoomIn className="h-4 w-4" />
          </button>
          <div className="border-t border-outline" />
          <button
            type="button"
            onClick={zoomOut}
            disabled={scale <= MIN_SCALE}
            className="flex h-8 w-8 items-center justify-center text-on-surface-variant hover:bg-surface-variant disabled:opacity-40"
            aria-label="Zoom out"
            title="Zoom out"
          >
            <ZoomOut className="h-4 w-4" />
          </button>
          <div className="border-t border-outline" />
          <button
            type="button"
            onClick={resetView}
            className="flex h-8 w-8 items-center justify-center text-on-surface-variant hover:bg-surface-variant"
            aria-label="Reset view"
            title="Reset zoom & position"
          >
            <Maximize className="h-4 w-4" />
          </button>
        </div>
        <div className="absolute bottom-3 right-3 z-10 rounded-full border border-outline bg-surface px-2.5 py-1 text-[11px] text-on-surface-variant shadow-sm">
          {Math.round(scale * 100)}%
        </div>

        <div
          className="flex origin-top-left flex-col items-center p-6"
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})`,
            transition: isPanning ? 'none' : 'transform 0.1s ease-out',
            width: 'max-content',
          }}
        >
          {data.roots.map((root, i) => (
            <div key={root.id} className={i > 0 ? 'mt-8' : ''}>
              <TreeNode
                node={root}
                search={search}
                collapsed={collapsed}
                onToggle={toggleCollapse}
                isAdmin={isAdmin}
                onReassign={setReassignTarget}
              />
            </div>
          ))}
        </div>
      </div>
      <p className="text-[11px] text-on-surface-variant">
        Scroll to pan, hold Ctrl/Cmd + scroll or use the controls to zoom, or drag to move around the chart.
      </p>

      {/* Reassign Manager Modal */}
      {reassignTarget && (
        <ReassignModal
          target={reassignTarget}
          allUsers={allUsers}
          onClose={() => setReassignTarget(null)}
        />
      )}
    </div>
  );
}

// ─── TreeNode (recursive) ─────────────────────────────────────────────────────

function TreeNode({
  node,
  search,
  collapsed,
  onToggle,
  isAdmin,
  onReassign,
}: {
  node: OrgNode;
  search: string;
  collapsed: Set<string>;
  onToggle: (id: string) => void;
  isAdmin: boolean;
  onReassign: (node: OrgNode) => void;
}) {
  const hasChildren = node.children.length > 0;
  const isCollapsed = collapsed.has(node.id);
  const isMatch = search ? nodeMatchesSearch(node, search) : false;
  const subtreeMatch = search ? treeContainsMatch(node, search) : true;

  if (search && !subtreeMatch) return null;

  const visibleChildren = search
    ? node.children.filter((c) => treeContainsMatch(c, search))
    : node.children;

  return (
    <div className="flex flex-col items-center">
      {/* Card */}
      <div
        className={`relative rounded-lg border-2 px-4 py-3 shadow-sm transition-all ${
          isMatch ? 'border-primary bg-primary-container/20 shadow-md' : 'border-outline bg-surface'
        }`}
        style={{ minWidth: '180px', maxWidth: '220px' }}
      >
        {/* Role color strip */}
        <div
          className="absolute left-0 top-0 h-full w-1 rounded-l-lg"
          style={{ backgroundColor: ROLE_COLORS[node.role] ?? '#6b7280' }}
        />

        {/* Avatar circle */}
        <div className="mb-1.5 flex items-center justify-center">
          <div
            className="flex h-10 w-10 items-center justify-center rounded-full text-sm font-bold text-white"
            style={{ backgroundColor: ROLE_COLORS[node.role] ?? '#6b7280' }}
          >
            {node.display_name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase()}
          </div>
        </div>

        {/* Name */}
        <p className="text-center text-data-sm font-semibold text-on-surface leading-tight">
          {node.display_name}
        </p>

        {/* Position or Role */}
        <p className="mt-0.5 text-center text-[10px] text-on-surface-variant">
          {node.position || ROLE_LABELS[node.role] || node.role}
        </p>

        {/* Department badge */}
        {node.department && (
          <div className="mt-1.5 flex justify-center">
            <Badge tone="info" className="text-[9px]">
              {DEPT_LABELS[node.department] ?? node.department}
            </Badge>
          </div>
        )}

        {/* Expand/collapse + reassign buttons */}
        <div className="mt-2 flex items-center justify-center gap-1">
          {hasChildren && (
            <button
              type="button"
              onClick={() => onToggle(node.id)}
              className="flex h-5 w-5 items-center justify-center rounded-full border border-outline bg-surface text-on-surface-variant hover:bg-surface-variant"
              aria-label={isCollapsed ? 'Expand' : 'Collapse'}
            >
              {isCollapsed ? <ChevronRight className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            </button>
          )}
          {isAdmin && (
            <button
              type="button"
              onClick={() => onReassign(node)}
              className="flex h-5 w-5 items-center justify-center rounded-full border border-outline bg-surface text-on-surface-variant hover:bg-surface-variant"
              aria-label="Change manager"
              title="Change reporting manager"
            >
              <UserCog className="h-3 w-3" />
            </button>
          )}
        </div>

        {/* Children count indicator */}
        {hasChildren && isCollapsed && (
          <p className="mt-1 text-center text-[9px] text-on-surface-variant">
            {node.children.length} report{node.children.length !== 1 ? 's' : ''}
          </p>
        )}
      </div>

      {/* Connector line down to children */}
      {hasChildren && !isCollapsed && visibleChildren.length > 0 && (
        <>
          {/* Vertical line from parent to horizontal bar */}
          <div className="h-6 w-px bg-outline" />

          {/* Children container */}
          {visibleChildren.length === 1 ? (
            <TreeNode
              node={visibleChildren[0]}
              search={search}
              collapsed={collapsed}
              onToggle={onToggle}
              isAdmin={isAdmin}
              onReassign={onReassign}
            />
          ) : (
            <div className="relative flex items-start gap-0">
              {/* Horizontal connector bar */}
              <div className="absolute left-1/2 top-0 -translate-x-1/2" style={{ width: `calc(100% - 180px)`, height: '1px', backgroundColor: 'var(--outline, #d4d4d8)' }} />

              <div className="flex gap-4">
                {visibleChildren.map((child) => (
                  <div key={child.id} className="flex flex-col items-center">
                    {/* Vertical connector from horizontal bar to child */}
                    <div className="h-6 w-px bg-outline" />
                    <TreeNode
                      node={child}
                      search={search}
                      collapsed={collapsed}
                      onToggle={onToggle}
                      isAdmin={isAdmin}
                      onReassign={onReassign}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Reassign Manager Modal ───────────────────────────────────────────────────

function ReassignModal({
  target,
  allUsers,
  onClose,
}: {
  target: OrgNode;
  allUsers: OrgNode[];
  onClose: () => void;
}) {
  const reassign = useReassignManager();
  const [selectedManager, setSelectedManager] = useState<string>(target.manager_id ?? '');

  const managerOptions = useMemo(() => {
    const opts = [{ value: '', label: '— No Manager (Root) —' }];
    for (const u of allUsers) {
      if (u.id === target.id) continue; // Can't be own manager
      opts.push({ value: u.id, label: `${u.display_name} (${ROLE_LABELS[u.role] ?? u.role})` });
    }
    return opts;
  }, [allUsers, target.id]);

  const handleSave = () => {
    reassign.mutate(
      { userId: target.id, managerId: selectedManager || null },
      {
        onSuccess: () => {
          toast.success(`Manager updated for ${target.display_name}.`);
          onClose();
        },
        onError: (err) => toast.error(getApiErrorMessage(err, 'Failed to reassign manager.')),
      },
    );
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Change Reporting Manager"
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} loading={reassign.isPending}>Save</Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="flex items-center gap-3 rounded border border-outline bg-surface-variant/30 px-3 py-2">
          <div
            className="flex h-9 w-9 items-center justify-center rounded-full text-xs font-bold text-white"
            style={{ backgroundColor: ROLE_COLORS[target.role] ?? '#6b7280' }}
          >
            {target.display_name.split(' ').map((w) => w[0]).join('').slice(0, 2).toUpperCase()}
          </div>
          <div>
            <p className="text-data-sm font-medium text-on-surface">{target.display_name}</p>
            <p className="text-[11px] text-on-surface-variant">
              {ROLE_LABELS[target.role] ?? target.role} · {DEPT_LABELS[target.department] ?? target.department}
            </p>
          </div>
        </div>

        <Select
          label="Reports to"
          name="new-manager"
          value={selectedManager}
          onChange={(e) => setSelectedManager(e.target.value)}
          options={managerOptions}
        />

        <p className="text-[11px] text-on-surface-variant">
          Changing the manager will move this person and their direct reports under the new manager in the org chart.
        </p>
      </div>
    </Modal>
  );
}
